import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { doNothing } from 'remeda';
import Sockette from 'sockette';

import config from '@@config';
import { getIsAuthenticated, useAuthStore } from '@@scripts/store/useAuthStore';
import { castArray } from '@@scripts/utils/arrayUtils';
import { addDatadogError } from '@@scripts/utils/dataDog';

const NORMAL_CLOSURE_CODE = 1000;
const GOING_AWAY_CODE = 1001;
const NO_STATUS_RECEIVED_CODE = 1005;
const ABNORMAL_CLOSURE_CODE = 1006;
const NORMAL_WEBSOCKET_CLOSE_CODES = new Set([
    NORMAL_CLOSURE_CODE,
    GOING_AWAY_CODE,
    NO_STATUS_RECEIVED_CODE,
]);
const WEBSOCKET_RETRY_DELAY_MS = 10_000;

type Options = {
    debug?: boolean;
    enabled?: boolean;
    onUpdate?: (data: Record<string, unknown>) => void;
};

type Payload = {
    path: string;
    queryKey: QueryKey | QueryKey[];
    params?: UnknownObject;
};

const useLiveUpdates = ({ path, params, queryKey }: Payload, options: Options = {}) => {
    const { debug = false, enabled = true, onUpdate = doNothing() } = options;

    const ws = useRef<Sockette | null>(null);
    const hasReportedConnectionFailure = useRef(false);

    const queryClient = useQueryClient();

    const accessToken = useAuthStore((state) => state.accessToken);
    const clearAuth = useAuthStore((state) => state.clearAuth);

    const isMultipleQueryKeys = Array.isArray(queryKey[0]);

    const url = new URL(`${config.microservices.websocket.baseUrl}${path}`);

    // @ts-expect-error URLSearchParams should accept numbers for values https://github.com/microsoft/TypeScript/issues/32951
    const urlParams = new URLSearchParams(params);

    for (const [key, value] of urlParams.entries()) {
        url.searchParams.append(key, value);
    }

    const href = url.href;

    useEffect(() => {
        const log = (label: string, event: Event | CloseEvent | MessageEvent) => {
            if (debug) {
                // oxlint-disable-next-line no-console
                console.log('useLiveUpdates', label, event);
            }
        };
        const queryKeys = (isMultipleQueryKeys ? queryKey : [queryKey]) as QueryKey[];

        if (enabled && accessToken) {
            ws.current = new Sockette(href, {
                timeout: WEBSOCKET_RETRY_DELAY_MS,
                protocols: accessToken,
                onopen: (e) => {
                    log('Connected!', e);
                },

                onmessage: (e) => {
                    log('Received:', e);

                    const data = JSON.parse(e.data);
                    const array = castArray(data);

                    array.forEach((item) => {
                        if (item.resourceOperation === 'DELETE') {
                            // CD2-9991: This is a temporary workaround to disable this invalidation on metadata list.
                            // TODO: Remove this condition once all 20min contents are deleted
                            if (item.resourceName !== 'metadata') {
                                queryKeys.forEach((key) => {
                                    queryClient.removeQueries({ queryKey: key });
                                });
                            }
                        } else {
                            queryKeys.forEach((key) => {
                                queryClient.invalidateQueries({ queryKey: key });
                            });
                        }
                    });

                    onUpdate(data);
                },
                onreconnect: (e) => log('Reconnecting...', e),
                onmaximum: (e) => log('Stop Attempting!', e),

                // as we dont get the reason of why connection in closed, and we need to redirect to login if auth token expires,
                // we can check on our side if the session expired or not, and if it did we redirect.
                onclose: (e) => {
                    log('Closed!', e);

                    if (
                        !NORMAL_WEBSOCKET_CLOSE_CODES.has(e.code) &&
                        // We ignore this code on purpose because it is the one sent by the backend every time they close the connection
                        // due to inactivity. This is not something they can easily control.
                        // Filtering it out here will avoid sending too many unnecessary logs to Datadog.
                        e.code !== ABNORMAL_CLOSURE_CODE &&
                        !hasReportedConnectionFailure.current
                    ) {
                        hasReportedConnectionFailure.current = true;
                        addDatadogError(new Error('WebSocket closed unexpectedly'), {
                            code: e.code,
                            path,
                            reason: e.reason,
                        });
                    }

                    if (!getIsAuthenticated()) {
                        clearAuth();
                    }
                },
                onerror: (e) => log('Error:', e),
            });
        }

        return () => {
            if (ws.current) {
                ws.current.close();
                ws.current = null;
            }
        };
    }, [accessToken, enabled, href, path]);
};

export default useLiveUpdates;
